import React, { useState } from "react";
import { motion } from "framer-motion";
import { X, CheckCircle2 } from "lucide-react";

// Feedback Modal Component
export function RejectFeedbackModal({ isOpen, onClose, onSubmit, roleTitle, onAccept }) {
  const [missingSkillsModalOpen, setMissingSkillsModalOpen] = useState(false);
  const feedbackOptions = [
    "Salary mismatch",
    "Wrong location or working style",
    "Industry mismatch",
    "Missing skills",
    "Missing role info",
    "Too many interviews",
    "Other",
  ];
  const [selected, setSelected] = useState("");
  const [otherText, setOtherText] = useState("");

  if (!isOpen) return null;

  const performSubmit = () => {
    if (selected === "Other") {
      if (otherText.trim()) {
        onSubmit({ reason: "Other", otherText: otherText });
      } else {
        return; // Or show an error, but button is disabled anyway
      }
    } else if (selected) {
      onSubmit({ reason: selected });
    }
    setSelected(""); 
    setOtherText("");
  };

  const handleSubmit = () => {
    if (selected === "Missing skills") {
      setMissingSkillsModalOpen(true);
      return;
    }
    performSubmit();
  };

  const isSubmitDisabled = !selected || (selected === "Other" && otherText.trim() === "");

  return (
    <>
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm selection:bg-[#02B491]/30">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Why are you declining?</h2>
          <button onClick={onClose} className="p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 text-sm mb-4">
          Your feedback helps us improve our matching process for <strong className="text-gray-700">{roleTitle}</strong>.
        </p>
        <div className="mb-4 space-y-2">
          {feedbackOptions.map(option => (
            <div key={option}>
              <label className="flex items-center gap-3 cursor-pointer text-sm text-gray-700">
                <input
                  type="radio"
                  name="feedback-reason"
                  value={option}
                  checked={selected === option}
                  onChange={() => setSelected(option)}
                  className="sr-only peer"
                />
                <span className="w-4 h-4 rounded-full border-2 border-gray-300 flex items-center justify-center peer-checked:bg-[#02B491] peer-checked:border-[#02B491] transition-colors">
                  <span className="w-2 h-2 rounded-full bg-white transform scale-0 peer-checked:scale-100 transition-transform"></span>
                </span>
                <span>{option}</span>
              </label>
              {option === "Other" && selected === "Other" && (
                <motion.div 
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-2 ml-7"
                >
                  <textarea
                    value={otherText}
                    onChange={(e) => setOtherText(e.target.value)}
                    placeholder="Please provide details"
                    className="w-full p-2 text-sm border-gray-300 rounded-md shadow-sm focus:ring-[#02B491] focus:border-[#02B491] transition"
                    rows={3}
                  />
                </motion.div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491]"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${!isSubmitDisabled ? 'bg-[#02B491] hover:bg-[#018268]' : 'bg-gray-300 cursor-not-allowed'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491]`}
            disabled={isSubmitDisabled}
          >
            Submit Feedback
          </button>
        </div>
      </motion.div>
    </div>
    <MissingSkillsConfirmationModal
        isOpen={missingSkillsModalOpen}
        onClose={() => setMissingSkillsModalOpen(false)}
        onConfirm={() => {
          if (onAccept) {
            onAccept();
          }
          setMissingSkillsModalOpen(false);
          onClose();
        }}
        onStillPass={() => {
          performSubmit();
        }}
      />
    </>
  );
}

// Calendly Booking Popup
export function CalendlyPopup({ open, onClose, company, calendlyUrl }) {
  if (!open || !calendlyUrl) return null;
  
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-xl font-semibold text-gray-900">
            📅 Book your interview with {company || "the company"}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full transition-colors hover:bg-gray-100"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Calendly Embed */}
        <div className="flex-1 min-h-0">
          <iframe
            src={calendlyUrl}
            className="w-full h-full min-h-[650px]"
            title={`Book interview with ${company}`}
            frameBorder="0"
          />
        </div>

        {/* Next Steps Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
          <div className="prose prose-sm text-gray-600 max-w-none">
            <p dangerouslySetInnerHTML={{ __html: '<strong>Next steps:</strong> You will have been introduced over email - please email the startup to confirm that you\'ve booked your interview. If there are any issues or you can\'t make any of the available slots, simply respond to the introduction email.' }} />
          </div>
        </div>

      </motion.div>
    </div>
  );
}

// Minimalist Thank You Popup (for when no Calendly link is available)
export function ThankYouPopup({ open, onClose, company }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full"
      >
        <div className="text-center">
          <CheckCircle2 className="w-10 h-10 text-[#02B491] mb-3 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Thank You for Responding!
          </h2>
          <p className="text-gray-700 text-center text-sm mb-4">
            We've let{" "}
            <span className="font-medium">{company || "the company"}</span> know
            about your response.
          </p>
        </div>

        <div className="mt-4 text-left p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div
            className="prose prose-sm max-w-none text-gray-800"
            dangerouslySetInnerHTML={{
              __html:
                "<strong>Next steps:</strong> You will be introduced over email. Please respond to that email to coordinate with the startup and schedule your interview.",
            }}
          />
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-2 bg-[#02B491] text-white rounded-lg text-sm font-medium hover:bg-[#018268] transition-colors"
        >
          Close
        </button>
      </motion.div>
    </div>
  );
}

// Missing Skills Confirmation Modal
export function MissingSkillsConfirmationModal({ isOpen, onClose, onConfirm, onStillPass }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm selection:bg-[#02B491]/30">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 30 }}
        className="bg-white w-full max-w-md p-6 rounded-xl shadow-xl"
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-800">Are you sure you want to pass?</h2>
          <button onClick={onClose} className="p-1 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491]">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 text-sm mb-4">
          The startup chose you - chances are, they see something in your profile that really fits what they're looking for. If you're on the fence, it's always worth having a first conversation!
        </p>
        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onStillPass}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491]"
          >
            Still Pass
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 text-sm font-medium text-white rounded-lg bg-[#02B491] hover:bg-[#018268] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#02B491]"
          >
            Ok - introduce me!
          </button>
        </div>
      </motion.div>
    </div>
  );
} 